import type { ResultOf } from "@graphql-typed-document-node/core";
import { type JSX, useCallback, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "urql";
import Loader from "../../shared/components/Loader";
import ShowError from "../../shared/components/ShowError";
import t from "../../shared/translations";
import { graphql } from "../gql";
import type { IBuilderParams } from "./BuilderRoutes";
import ExamplePath from "./ExamplePath";

const query = graphql(`
    query BuilderPathListQuery($locale: Language!) {
        builderPathList(locale: $locale) {
            id
            name
            publicId
            locale
            description
            showThemes
            showMiles
            showLength
            showPrice
            builderTaskOnPaths {
                id
                builderTask {
                    id
                    icon
                    shortTitle
                    shortDescription
                    miles
                    length
                    learningCoachFeedback
                    price
                    isGroupTask
                    isMedia
                    isPeopleEvent
                    isLecture
                    themes {
                        id
                        theme
                        value
                    }
                }
            }
        }
    }
`);

export type IExampleTask = ResultOf<
    typeof query
>["builderPathList"][number]["builderTaskOnPaths"][number]["builderTask"];

export type IExamplePath = ResultOf<typeof query>["builderPathList"][number];

function ExamplesModal(): JSX.Element {
    const { lang } = useParams<IBuilderParams>();
    const [open, setOpen] = useState<string>("");
    const [result] = useQuery({
        query,
        variables: {
            locale: lang,
        },
    });

    const toggleOpen = useCallback((id: string) => {
        setOpen((openId) => {
            if (id === openId) {
                return "";
            }
            return id;
        });
    }, []);

    if (result.fetching) {
        return <Loader />;
    }
    if (result.error) {
        return <ShowError error={t("public.error.error")} />;
    }
    const paths = result.data?.builderPathList;
    return (
        <>
            <h2>{t("public.builder.heading-example-paths")}</h2>
            {paths.map((path) => (
                <ExamplePath key={path.id} path={path} toggleOpen={toggleOpen} isOpen={path.id === open} />
            ))}
        </>
    );
}

export default ExamplesModal;
